import { Spacer } from "@hackthenorth/north";
import { PlusIcon } from "@heroicons/react/24/outline";
import debounce from "lodash.debounce";
import { Button } from "north.js";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { useProfileContext } from "src/shared/contexts/ProfileContext";
import { HackerAPIFile } from "src/shared/utils/hackerapi";
import { useDiscoveryProfileIdsLazyQuery } from "src/views/hacker-directory/graphql/discoveryProfileIds.generated";
import { useGetDiscoveryProfilesLazyQuery } from "src/views/hacker-directory/graphql/getDiscoveryProfiles.generated";
import { mapDiscoveryProfile } from "src/views/hacker-directory/shared/utils";
import EmptyTeammatesPlaceholder from "src/views/profile/components/EmptyTeammatesPlaceholder";
import ProfileTag from "src/views/profile/components/ProfileTag";
import ProfileTypeahead, {
  ProfileOption,
} from "src/views/profile/components/ProfileTypeahead";

import { MAX_TEAM_SIZE } from "../constants";
import { actionButtonTextStyles, subtitleTextStyles } from "../styles";
import { ProfileFormValues } from "../types";

const MAX_TEAMMATES = MAX_TEAM_SIZE - 1;

export interface TeammatesProps {
  staging: ProfileFormValues;
  setStagingField: <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => void;
}

const Teammates: React.FC<TeammatesProps> = ({ staging, setStagingField }) => {
  const { profile } = useProfileContext();
  const isPublished = profile?.isPublished ?? false;
  const authClaimId = profile?.claimId;
  const teammateIds = staging.teammates_claim_ids ?? [];
  const teamFull = teammateIds.length >= MAX_TEAMMATES;

  // ProfileTypeahead owns the input; this only gates results/notices.
  const [hasSearchQuery, setHasSearchQuery] = useState(false);
  const [getDiscoveryProfileIds, idsQuery] = useDiscoveryProfileIdsLazyQuery();
  const [getDiscoveryProfiles, profilesQuery] =
    useGetDiscoveryProfilesLazyQuery();

  const debouncedGetIds = useMemo(
    () =>
      debounce((textQuery: string) => {
        void getDiscoveryProfileIds({
          variables: {
            interests: [],
            weekendPace: [],
            teammatesNeeded: [],
            query: { lexical: textQuery },
          },
        });
      }, 500),
    [getDiscoveryProfileIds]
  );

  useEffect(() => () => debouncedGetIds.cancel(), [debouncedGetIds]);

  useEffect(() => {
    const ids = (idsQuery.data?.discoveryProfileIds ?? []).filter(
      (id) => id !== authClaimId
    );
    if (ids.length === 0) return;
    void getDiscoveryProfiles({
      variables: {
        profileIds: ids.slice(0, 20),
        pipelineSlug: `${CURRENT_EVENT_SLUG}-discovery-profiles`,
      },
    });
  }, [idsQuery.data, authClaimId, getDiscoveryProfiles]);

  const profileOptions: ProfileOption[] = useMemo(() => {
    if (!hasSearchQuery) return [];
    // Fetch effect skips empty ids, so profilesQuery can be stale — gate here.
    const matchedIds = idsQuery.data?.discoveryProfileIds?.filter(
      (id) => id !== authClaimId
    );
    if (matchedIds && matchedIds.length === 0) return [];

    return (profilesQuery.data?.profiles ?? [])
      .filter((p) => p.id !== authClaimId)
      .map((p) => {
        const mapped = mapDiscoveryProfile(p.id, p.fields);
        return {
          claimId: mapped.id,
          name: mapped.name?.trim() || "Unknown",
          profilePhoto: (mapped.profilePhoto ?? {}) as HackerAPIFile,
          bio: mapped.bio ?? "",
        };
      });
  }, [hasSearchQuery, idsQuery.data, profilesQuery.data, authClaimId]);

  const loading = idsQuery.loading || profilesQuery.loading;

  const removeTeammate = (id: string) =>
    setStagingField(
      "teammates_claim_ids",
      teammateIds.filter((value) => value !== id)
    );

  let resultsNotice: React.ReactNode = null;
  if (hasSearchQuery) {
    if (loading) {
      resultsNotice = "Searching…";
    } else if (idsQuery.error || profilesQuery.error) {
      resultsNotice = "Couldn't load profiles. Try again.";
    } else if (teamFull) {
      resultsNotice = (
        <TeamFullNotice>
          Your team is full! Remove a teammate if you need to make changes.
        </TeamFullNotice>
      );
    } else if (profileOptions.length === 0) {
      resultsNotice =
        "No hackers found with that name. Make sure they've created their directory profile!";
    }
  }

  return (
    <>
      {teammateIds.length === 0 ? (
        <EmptyTeammatesPlaceholder />
      ) : (
        <TeammateCards>
          {teammateIds.map((id) => (
            <ProfileTag
              key={id}
              claimId={Number(id)}
              authenticatedClaimId={authClaimId}
              authenticatedTeammateClaimIds={teammateIds}
              profileOwnerName={staging.name}
              isOwnProfile
              onRemove={isPublished ? () => removeTeammate(id) : undefined}
            />
          ))}
        </TeammateCards>
      )}

      <Spacer height={16} />

      {!isPublished ? (
        <Notice>
          Teammate search becomes available after you publish your profile.
        </Notice>
      ) : (
        <ProfileTypeahead
          profileOptions={profileOptions}
          onSearchChange={(value) => {
            const trimmed = value.trim();
            setHasSearchQuery(trimmed !== "");
            if (!trimmed) return;
            debouncedGetIds(trimmed);
          }}
          resultsNotice={resultsNotice}
          renderAction={(option) => {
            const id = String(option.claimId);
            const selected = teammateIds.includes(id);
            return (
              <TeammateActionButton
                color={selected ? "tertiary" : "secondary"}
                size="lg"
                trailingIcon={
                  selected ? undefined : (
                    <PlusIcon width={16} height={16} aria-hidden />
                  )
                }
                disabled={!selected && teamFull}
                onClick={() => {
                  if (selected) {
                    removeTeammate(id);
                    return;
                  }
                  if (teamFull) return;
                  setStagingField("teammates_claim_ids", [...teammateIds, id]);
                }}
              >
                {selected ? "Added" : "Add"}
              </TeammateActionButton>
            );
          }}
        />
      )}
    </>
  );
};

export default Teammates;

const TeammateCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 104px);
  align-items: start;
  gap: 12px;
`;

const TeammateActionButton = styled(Button)`
  ${actionButtonTextStyles}
`;

const Notice = styled.p`
  ${subtitleTextStyles}
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const TeamFullNotice = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
`;
